import { useState } from "react";
import * as Icons from "lucide-react";

const CONF_TONE = {
  High: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Medium: "text-amber-700 bg-amber-50 border-amber-200",
  Low: "text-rose-700 bg-rose-50 border-rose-200",
};

function TraceList({ label, items, icon: Icon, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" />{label}
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-[13px] text-slate-600">
            <Icons.Dot className="h-4 w-4 text-slate-300 shrink-0 -ml-1" /><span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// rec = data.estimate_recommendation (see backend estimate_recommendation.build)
export function EstimateGovernance({ rec }) {
  const [showTrace, setShowTrace] = useState(false);
  if (!rec) return null;

  const ownership = rec.ownership || {};
  const mgmt = rec.management_review || {};
  const trace = rec.decision_trace || {};
  const confTone = CONF_TONE[rec.confidence] || CONF_TONE.Medium;
  const withinProfile = !!rec.within_profile;
  const flagged = (rec.boundary_checks || []).filter((b) => !b.in_scope);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6" data-testid="ca-estimate-governance">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
            <Icons.ScrollText className="h-4.5 w-4.5 text-indigo-600" />
          </span>
          <div>
            <div className="text-sm font-semibold text-slate-800">Estimate Recommendation</div>
            <div className="text-[11px] text-slate-400">Governed advisory · pricing owned by the Service Library</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200" data-testid="ca-eg-status">
            {rec.status || "Advisory"}
          </span>
          <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${confTone}`} data-testid="ca-eg-confidence">
            {rec.confidence} confidence
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* ---- Ownership stamp ---- */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="ca-eg-ownership">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
            <Icons.Users className="h-3.5 w-3.5" />Engagement Ownership
          </div>
          {ownership.primary_team ? (
            <>
              <div className="flex items-center gap-2 text-sm">
                <Icons.Building2 className="h-4 w-4 text-blue-600 shrink-0" />
                <span className="text-slate-500">Primary:</span>
                <span className="font-semibold text-slate-800" data-testid="ca-eg-primary-team">{ownership.primary_team}</span>
              </div>
              {(ownership.collaborating_teams || []).length > 0 && (
                <div className="flex items-start gap-2 text-sm mt-1.5" data-testid="ca-eg-collaborating">
                  <Icons.UsersRound className="h-4 w-4 text-violet-600 shrink-0 mt-0.5" />
                  <span className="text-slate-500 shrink-0">Collaborating:</span>
                  <span className="font-medium text-slate-700">{ownership.collaborating_teams.join(", ")}</span>
                </div>
              )}
              <div className="text-[11px] text-slate-400 mt-2">
                Routing confidence: <span className="font-medium text-slate-500">{ownership.routing_confidence || "—"}</span>
                {ownership.routing_type ? ` · ${ownership.routing_type}` : ""}
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-500" data-testid="ca-eg-ownership-none">
              {ownership.reason || "Confirm the engagement's U.S./Thai tax angle before assigning an owner."}
            </div>
          )}
        </div>

        {/* ---- Practice Profile boundary + management review ---- */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="ca-eg-boundary">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
            <Icons.ShieldCheck className="h-3.5 w-3.5" />Practice Profile
          </div>
          {withinProfile ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="ca-eg-within-profile">
              <Icons.CheckCircle2 className="h-4 w-4 shrink-0" />Within published capabilities
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="ca-eg-flagged">
              <div className="flex items-center gap-2 text-sm text-amber-700 font-medium">
                <Icons.AlertTriangle className="h-4 w-4 shrink-0" />Review boundary for {flagged.length} service{flagged.length > 1 ? "s" : ""}
              </div>
              {flagged.map((b, i) => (
                <div key={i} className="text-[12px] text-amber-700/90 pl-6" data-testid={`ca-eg-flag-${i}`}>
                  <span className="font-semibold">{b.service}</span> — {b.reason}
                </div>
              ))}
              <div className="text-[11px] text-slate-400 pl-6">Advisory only — no service was removed.</div>
            </div>
          )}
          {mgmt.required && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2" data-testid="ca-eg-mgmt-review">
              <Icons.Flag className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-[12px] text-amber-800">
                <span className="font-semibold">Management review recommended.</span>
                {mgmt.reason ? <span className="block text-amber-700/90 mt-0.5">{mgmt.reason}</span> : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---- AI Decision Trace ---- */}
      <div className="mt-4 border-t border-slate-100 pt-3">
        <button onClick={() => setShowTrace((s) => !s)} data-testid="ca-eg-trace-toggle"
          className="text-xs font-semibold text-indigo-600 hover:underline inline-flex items-center gap-1">
          <Icons.GitBranch className="h-3.5 w-3.5" />AI Decision Trace
          <Icons.ChevronDown className={`h-3.5 w-3.5 transition-transform ${showTrace ? "rotate-180" : ""}`} />
        </button>
        {showTrace && (
          <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 space-y-3" data-testid="ca-eg-trace">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Decision</div>
              <div className="text-[13px] text-slate-700 font-medium">{trace.decision}</div>
            </div>
            <TraceList label="Evidence" items={trace.evidence} icon={Icons.FileSearch} testid="ca-eg-trace-evidence" />
            <TraceList label="Reasoning" items={trace.reasoning} icon={Icons.Brain} testid="ca-eg-trace-reasoning" />
            {(trace.applicable_policies || []).length > 0 && (
              <div data-testid="ca-eg-trace-policies">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1.5"><Icons.BookMarked className="h-3.5 w-3.5" />Applicable Policies</div>
                <div className="flex flex-wrap gap-1.5">
                  {trace.applicable_policies.map((p, i) => <span key={i} className="text-[11px] bg-white border border-indigo-200 text-indigo-700 rounded px-1.5 py-0.5">{p}</span>)}
                </div>
              </div>
            )}
            {(trace.inputs_consumed || []).length > 0 && (
              <div data-testid="ca-eg-trace-inputs">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1.5"><Icons.Boxes className="h-3.5 w-3.5" />Inputs Consumed</div>
                <div className="flex flex-wrap gap-1.5">
                  {trace.inputs_consumed.map((p, i) => <span key={i} className="text-[11px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{p}</span>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
